import React, { useEffect, useState } from "react";
import { BASE_URL } from "../../Config/api.config";
import useLocalStorage from "../../Hooks/useLocalStorage";
import { TbLoader2 } from "react-icons/tb";
import {
  FaServer,
  FaUsers,
  FaUserTie,
  FaUser,
  FaHistory,
} from "react-icons/fa";

import { FaCheck } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import { GrServices } from "react-icons/gr";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const FaServerIcon = FaServer as unknown as IconComponent;
const FaUsersIcon = FaUsers as unknown as IconComponent;
const FaUserTieIcon = FaUserTie as unknown as IconComponent;
const FaUserIcon = FaUser as unknown as IconComponent;
const FaHistoryIcon = FaHistory as unknown as IconComponent;
const FaCheckIcon = FaCheck as unknown as IconComponent;
const IoCloseIcon = IoClose as unknown as IconComponent;
const GrServicesIcon = GrServices as unknown as IconComponent;
const TbLoader2Icon = TbLoader2 as unknown as IconComponent;

interface LiveUser {
  user_id: string;
  token: string;
  date_created: string;
  last_seen_at: string;
  platform: string | null;
  ip_address: string | null;
  user_agent: string | null;
  first_name: string;
  last_name: string;
  client_type: string;
}

interface LiveUsersData {
  window_minutes: number;
  threshold: string;
  active_sessions: number;
  unique_users: number;
  active_employers: number;
  active_workers: number;
  recent_sessions: LiveUser[];
}

interface ApiRequest {
  id: string;
  endpoint: string;
  method: string;
  status_code: string;
  token: string;
  user_id: string;
  first_name: string;
  last_name: string;
  client_type: string;
  request_uri: string;
  ip_address: string;
  user_agent: string;
  request_payload: string | null;
  response_time_ms: string | null;
  date_created: string;
}

const Services = () => {
  const [adminData] = useLocalStorage("adminData", "");
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [liveUsers, setLiveUsers] = useState<LiveUsersData | null>(null);
  const [requests, setRequests] = useState<ApiRequest[]>([]);
  const [activeTab, setActiveTab] = useState<"services" | "requests">("services");
  const [requestEndpoint, setRequestEndpoint] = useState("");
  const [debouncedRequestEndpoint, setDebouncedRequestEndpoint] = useState("");
  const [requestLimit, setRequestLimit] = useState("25");
  const [requestMethod, setRequestMethod] = useState("GET");
  const [totals, setTotals] = useState({
    totalEnabledServices: 0,
    totalOkServices: 0,
    totalErrorServices: 0,
  });

  const getMonitoring = async (showLoader = false) => {
    try {
      if (showLoader) {
        setLoading(true);
      }

      const res = await fetch(`${BASE_URL}adm.sys.services`, {
        headers: {
          Authorization: `Bearer ${adminData.uid}`,
        },
      });
      const data = await res.json();
      if (data?.status === "ok") {
        setServices(data.result.monitor || []);
        setLiveUsers(data.result.liveUsers || null);
        setTotals({
          totalEnabledServices: data.result.totalEnabledServices,
          totalOkServices: data.result.totalOkServices,
          totalErrorServices: data.result.totalErrorServices,
        });
      }
    } catch (err) {
      console.error("Error fetching services:", err);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  const getRequests = async (
    options: {
      endpoint?: string;
      limit?: string;
      method?: string;
    } = {}
  ) => {
    const {
      endpoint = debouncedRequestEndpoint,
      limit = requestLimit,
      method = requestMethod,
    } = options;

    try {
      const requestParams = new URLSearchParams({
        ...(endpoint ? { endpoint } : {}),
        ...(method ? { method } : {}),
        ...(limit ? { limit } : {}),
      });

      const res = await fetch(`${BASE_URL}adm.api.requests.php?${requestParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${adminData.uid}`,
        },
      });
      const data = await res.json();
      if (data?.status === "ok") {
        setRequests(data.result.requests || []);
      }
    } catch (err) {
      console.error("Error fetching requests:", err);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedRequestEndpoint(requestEndpoint.trim());
    }, 500);

    return () => clearTimeout(timer);
  }, [requestEndpoint]);

  useEffect(() => {
    getMonitoring(true);
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      getMonitoring();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    getRequests({
      endpoint: debouncedRequestEndpoint,
      limit: requestLimit,
      method: requestMethod,
    });

    const interval = setInterval(() => {
      getRequests({
        endpoint: debouncedRequestEndpoint,
        limit: requestLimit,
        method: requestMethod,
      });
    }, 30000);

    return () => clearInterval(interval);
  }, [debouncedRequestEndpoint, requestLimit, requestMethod]);

  const renderStatusIcon = (status: string) => {
    if (status === "1") return <FaCheckIcon className="text-green-500" />;
    if (status === "0") return <IoCloseIcon className="text-red-500" />;
    return null;
  };

  const UptimeBars = ({ lastResults }: { lastResults: string }) => {
    const results = (lastResults || "").split(",").slice(-30);
    return (
      <div className="flex gap-0.5">
        {results.map((r, i) => (
          <Tooltip
            key={i}
            title={r === "200" ? "Success" : "Error"}
            placement="top"
          >
            <div
              className={`w-2 h-6 rounded-sm ${r === "200" ? "bg-green-500" : "bg-red-500"
                }`}
            />
          </Tooltip>
        ))}
      </div>
    );
  };

  const getClientTypeIcon = (clientType: string) => {
    if (clientType === "2") return <FaUserTieIcon className="text-blue-500" />;
    if (clientType === "1") return <FaUserIcon className="text-green-500" />;
    return <FaUsersIcon className="text-gray-500" />;
  };

  const getClientTypeName = (clientType: string) => {
    if (clientType === "2") return "Employer";
    if (clientType === "1") return "Worker";
    return "Admin";
  };

  const getMethodColor = (method: string) => {
    switch (method) {
      case "GET":
        return "bg-green-100 text-green-700";
      case "POST":
        return "bg-blue-100 text-blue-700";
      case "PUT":
        return "bg-yellow-100 text-yellow-700";
      case "DELETE":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="h-[20vh] flex items-center justify-center bg-white rounded-lg shadow">
        <TbLoader2Icon className="h-10 w-10 text-[#2563EB] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex gap-4 items-center mb-6">
        <div className="p-3 bg-gradient-to-r from-[#2563EB] to-[#ed4685] rounded-lg shadow-lg">
          <FaServerIcon className="h-6 w-6 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-gray-800">System Services</h1>
      </div>

      {/* Live Users Section */}
      {liveUsers && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-[#2563EB] to-[#1e4bbf] px-6 py-4">
            <div className="flex items-center gap-3">
              <FaUsersIcon className="h-5 w-5 text-white" />
              <h2 className="text-lg font-semibold text-white">Live Users</h2>
              <span className="text-white/80 text-sm ml-auto">
                Last {liveUsers.window_minutes} minutes
              </span>
            </div>
          </div>

          <div className="p-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Active Sessions</p>
                    <p className="text-2xl font-bold text-blue-700">{liveUsers.active_sessions}</p>
                  </div>
                  <FaUsersIcon className="h-8 w-8 text-blue-400" />
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Unique Users</p>
                    <p className="text-2xl font-bold text-purple-700">{liveUsers.unique_users}</p>
                  </div>
                  <FaUsersIcon className="h-8 w-8 text-purple-400" />
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Active Employers</p>
                    <p className="text-2xl font-bold text-green-700">{liveUsers.active_employers}</p>
                  </div>
                  <FaUserTieIcon className="h-8 w-8 text-green-400" />
                </div>
              </div>

              <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-600 font-medium">Active Workers</p>
                    <p className="text-2xl font-bold text-orange-700">{liveUsers.active_workers}</p>
                  </div>
                  <FaUserIcon className="h-8 w-8 text-orange-400" />
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Since</p>
                    <p className="text-sm font-medium text-gray-700">
                      {new Date(liveUsers.threshold).toLocaleTimeString()}
                    </p>
                  </div>
                  <FaServerIcon className="h-8 w-8 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Recent Sessions Table */}
            <div>
              <h3 className="text-md font-semibold text-gray-700 mb-3">Recent Active Sessions</h3>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow className="bg-gray-50">
                      <TableCell className="font-semibold">Type</TableCell>
                      <TableCell className="font-semibold">Name</TableCell>
                      <TableCell className="font-semibold">Last Seen</TableCell>
                      <TableCell className="font-semibold">Token (truncated)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {liveUsers.recent_sessions.slice(0, 20).map((session) => (
                      <TableRow key={session.token} hover>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getClientTypeIcon(session.client_type)}
                            <span className="text-sm">{getClientTypeName(session.client_type)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">
                            {session.first_name} {session.last_name}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            (ID: {session.user_id})
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(session.last_seen_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={session.token} placement="top">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {session.token.substring(0, 16)}...
                            </code>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {liveUsers.recent_sessions.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No active sessions found
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab("services")}
            className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === "services"
              ? "text-[#2563EB] border-b-2 border-[#2563EB]"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            <GrServicesIcon className="inline mr-2" />
            System Services ({services.length})
          </button>
          <button
            onClick={() => setActiveTab("requests")}
            className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === "requests"
              ? "text-[#2563EB] border-b-2 border-[#2563EB]"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            <FaHistoryIcon className="inline mr-2" />
            API Requests ({requests.length})
          </button>
        </nav>
      </div>

      {/* Service Totals (only show when services tab is active) */}
      {activeTab === "services" && (
        <div className="bg-white rounded-md p-4 shadow flex items-center gap-6 text-gray-700">
          <p>
            <b>{totals.totalEnabledServices}</b> Enabled
          </p>
          <p>
            <b>{totals.totalOkServices}</b> OK
          </p>
          <p>
            <b>{totals.totalErrorServices}</b> Errors
          </p>
        </div>
      )}

      {/* Services Table */}
      {activeTab === "services" && (
        <>
          {services.length > 0 ? (
            <TableContainer component={Paper} elevation={0} className="shadow rounded-lg">
              <Table>
                <TableHead>
                  <TableRow className="bg-gray-50">
                    <TableCell className="font-semibold text-gray-700">Status</TableCell>
                    <TableCell className="font-semibold text-gray-700">Service Name</TableCell>
                    <TableCell className="font-semibold text-gray-700">Last Run</TableCell>
                    <TableCell className="font-semibold text-gray-700">Result</TableCell>
                    <TableCell className="font-semibold text-gray-700">Details</TableCell>
                    <TableCell className="font-semibold text-gray-700">History</TableCell>
                    <TableCell className="font-semibold text-gray-700">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {services.map((srv: any, i) => (
                    <TableRow key={srv.service_id} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <TableCell>{renderStatusIcon(srv.status)}</TableCell>
                      <TableCell>
                        <span className="font-medium text-gray-900">{srv.name_service}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">{srv.last_run_date || "—"}</span>
                      </TableCell>
                      <TableCell>
                        {srv.last_run_result_id === "200" ? (
                          <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Success
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Error
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600 truncate max-w-[200px]">
                        {srv.last_run_result_details || "—"}
                      </TableCell>
                      <TableCell>
                        <UptimeBars lastResults={srv.last_results} />
                      </TableCell>
                      <TableCell>
                        <button
                          className="inline-flex items-center gap-2 px-3 py-1 bg-[#2563EB] text-white rounded-lg hover:bg-[#1e4bbf] transition-colors text-sm shadow"
                          onClick={() => console.log("Run service:", srv.service_id)}
                        >
                          <GrServicesIcon className="w-4 h-4" />
                          Run
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <div className="p-4 text-center bg-white rounded shadow">
              <Typography>No services available</Typography>
            </div>
          )}
        </>
      )}

      {/* Requests Table */}
      {activeTab === "requests" && (
        <>
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <TextField
                label="Request endpoint"
                value={requestEndpoint}
                onChange={(e) => setRequestEndpoint(e.target.value)}
                size="small"
                fullWidth
                placeholder="worker.applied.jobs"
              />

              <FormControl size="small" fullWidth>
                <InputLabel id="request-limit-label">Limit</InputLabel>
                <Select
                  labelId="request-limit-label"
                  label="Limit"
                  value={requestLimit}
                  onChange={(e) => setRequestLimit(e.target.value as string)}
                >
                  <MenuItem value="10">10</MenuItem>
                  <MenuItem value="25">25</MenuItem>
                  <MenuItem value="50">50</MenuItem>
                  <MenuItem value="100">100</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel id="request-method-label">Method</InputLabel>
                <Select
                  labelId="request-method-label"
                  label="Method"
                  value={requestMethod}
                  onChange={(e) => setRequestMethod(e.target.value as string)}
                >
                  <MenuItem value="GET">GET</MenuItem>
                  <MenuItem value="POST">POST</MenuItem>
                  <MenuItem value="PUT">PUT</MenuItem>
                  <MenuItem value="DELETE">DELETE</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>

          {requests.length > 0 ? (
            <TableContainer component={Paper} elevation={0} className="shadow rounded-lg">
              <Table>
                <TableHead>
                  <TableRow className="bg-gray-50">
                    <TableCell className="font-semibold text-gray-700">ID</TableCell>
                    <TableCell className="font-semibold text-gray-700">Method</TableCell>
                    <TableCell className="font-semibold text-gray-700">Endpoint</TableCell>
                    <TableCell className="font-semibold text-gray-700">Status</TableCell>
                    <TableCell className="font-semibold text-gray-700">User</TableCell>
                    <TableCell className="font-semibold text-gray-700">IP Address</TableCell>
                    <TableCell className="font-semibold text-gray-700">Date Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((req, i) => (
                    <TableRow key={req.id} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"} hover>
                      <TableCell className="text-sm text-gray-600">{req.id}</TableCell>
                      <TableCell>
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getMethodColor(req.method)}`}>
                          {req.method}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={req.request_uri} placement="top">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {req.endpoint}
                          </code>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {req.status_code === "200" ? (
                          <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {req.status_code}
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {req.status_code}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getClientTypeIcon(req.client_type)}
                          <span className="text-sm">
                            {req.first_name} {req.last_name}
                          </span>
                          <span className="text-xs text-gray-500">
                            (ID: {req.user_id})
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={req.user_agent} placement="top">
                          <code className="text-xs text-gray-600">{req.ip_address}</code>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {new Date(req.date_created).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <div className="p-4 text-center bg-white rounded shadow">
              <Typography>No API requests found</Typography>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Services;
